import React from "react";
import { graphql } from "gatsby";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import classNames from "classnames";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import * as c from "./landingPage.module.scss";
import Layout from "@components/layout";
import {
  LpBlockquoteSection,
  LpFaqSection,
  LpLogoSoupSection,
  LpOneColSection,
  LpThreeColSection,
  LpTwoColSection,
  LpCompetitorSection,
  LpAtAGlance,
} from "@components/shared/landingPage";
import { generateFaqSchema, getProcessedFaqs } from "@components/shared/faqs";
import { CTASection, EmailMarketingSection, CarouselLarge } from "@components/shared";
import WistiaVideoEmbed from "@components/wistia/wistiaVideoEmbed";
import { PageHeader } from "@components/shared/pageHeader";
import PaymentsRateSection from "@components/pages/payments/paymentsRateSection";
import { ExploreMangomint } from "@components/shared/exploreMangomint";
import { competitorComparisonPageSlugs } from "@utils/competitorComparisonPage";
import { SeoMetadata } from "@components/shared/seoMetadata";

/*
  This template is currently used for:
  - Standard Landing Pages  |  `/features/<slug>`
  - Showcase Landing Pages  |  `/features/<slug>`
  - General Landing Pages   |  `/go/<slug>`
  - Business Landing Pages  |  `/solutions/<slug>`
*/

export default function LandingPage({ data: { contentfulLandingPage }, pageContext }) {
  const { pageVariant, pageHeader, actionSections, sections, embeddedWistiaVideoId, embeddedWistiaVideoTitle, slug } =
    contentfulLandingPage;

  const { isShowcasePage, isFeaturePage, isPaymentsPage, isDirectMailPage } = pageContext;

  const { desktopImage, mobileImage, backgroundImage, mobileBackgroundImage, tabletBackgroundImage, backgroundColor } =
    pageHeader || {};

  const pageHeaderImages = {
    mobile: mobileImage,
    desktop: desktopImage,
    background: backgroundImage,
    backgroundTablet: tabletBackgroundImage,
    backgroundMobile: mobileBackgroundImage,
    backgroundColor: backgroundColor,
  };

  const getSectionComponent = ({ id, __typename, ...props }) => {
    switch (__typename) {
      case "ContentfulLandingPageSectionLogoSoup":
        return <LpLogoSoupSection className={c.logoSoup} key={id} {...props} />;
      case "ContentfulLandingPageSectionAtAGlance":
        return <LpAtAGlance key={id} {...props} />;
      case "ContentfulLandingPageSection1Column1Image":
        return <LpOneColSection key={id} {...props} />;
      case "ContentfulLandingPageSection2Column1Image":
        return <LpTwoColSection key={id} {...props} isFeaturePage={isFeaturePage} />;
      case "ContentfulLandingPageSection3Column3Images":
        return <LpThreeColSection key={id} {...props} />;
      case "ContentfulLandingPageSectionBlockquote":
        return <LpBlockquoteSection key={id} {...props} />;
      case "ContentfulLandingPageSectionCarousel":
        return <CarouselLarge key={id} {...props} />;
      default:
        return null;
    }
  };

  const getActionSectionsComponent = ({ id, __typename, ...props }) => {
    switch (__typename) {
      case "ContentfulLandingPageSectionFooterCta":
        return <CTASection className={c.ctaSection} key={id} {...props} />;
      case "ContentfulLandingPageSectionEmailCapture":
        return <EmailMarketingSection key={id} {...props} />;
      case "ContentfulGenericList": {
        return props.listItems?.[0]?.__typename === "ContentfulListItemFaq" ? (
          <LpFaqSection key={id} items={props.listItems} {...props} />
        ) : null;
      }
      case "ContentfulExploreMangomint": {
        return <ExploreMangomint key={id} {...props} />;
      }
      case "ContentfulLandingPageSectionLogoSoup":
        return <LpLogoSoupSection className={c.logoSoup} key={id} {...props} />;
      default:
        return null;
    }
  };

  const sectionComponents = sections?.length >= 1 && sections.map((props) => getSectionComponent(props));
  const actionSectionsComponents =
    actionSections?.length >= 1 && actionSections.map((props) => getActionSectionsComponent(props));

  useGSAP(
    () => {
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      gsap.config({
        autoSleep: 60,
      });
      gsap.set("[data-transition='slide-enter'], [data-transition='slide-enter-child'] > *", {
        y: 50,
        opacity: 0,
        scale: 0.95,
        willChange: "transform",
      });
      ScrollTrigger.batch("[data-transition='slide-enter'], [data-transition='slide-enter-child'] > *", {
        start: () => "top 105%",
        end: () => "top 100%-=25px",
        invalidateOnRefresh: true,
        onLeave: (elements) => {
          gsap.to(elements, {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: reduceMotion ? 0 : 0.5,
            overwrite: true,
            stagger: {
              amount: reduceMotion ? 0 : 0.05,
            },
          });
        },
        onLeaveBack: (elements) => {
          gsap.set(elements, {
            y: 50,
            opacity: 0,
            scale: 0.95,
          });
        },
      });

      ScrollTrigger.config({ ignoreMobileResize: true });
    },
    { dependencies: [sectionComponents] }
  );

  const isCompetitorComparisonPage = competitorComparisonPageSlugs.includes(slug);

  return (
    <Layout
      navColorVariant={pageHeader?.darkBackground ? "cream-100" : pageHeaderImages.backgroundColor?.value || "default"}
      className={classNames(
        c.pageWrapper,
        "cream-100",
        (pageHeaderImages.background || pageHeader?.darkBackground) && "cream-header",
        pageHeaderImages.background && pageHeaderImages.backgroundColor?.value && "custom-bg"
      )}
    >
      {pageHeader && !isCompetitorComparisonPage && (
        <PageHeader
          className={classNames(c.pageHeader, (pageHeaderImages.background || pageHeader?.darkBackground) && c.bgImg)}
          pageVariant={pageVariant}
          isFeaturePage={isFeaturePage}
          isShowcasePage={isShowcasePage}
          isPaymentsPage={isPaymentsPage}
          isDirectMailPage={isDirectMailPage}
          pageHeaderData={pageHeader}
          currentImages={pageHeaderImages}
          includeWave={isDirectMailPage ? false : true}
        />
      )}

      {isCompetitorComparisonPage && <LpCompetitorSection slug={slug} />}

      <div className={c.sections}>
        {embeddedWistiaVideoId && (
          <section className={c.wistiaEmbed}>
            <h4 className="h4">{embeddedWistiaVideoTitle}</h4>
            <WistiaVideoEmbed
              wistiaOptions={"seo=false controlsVisibleOnLoad=false"}
              wistiaVideoId={embeddedWistiaVideoId}
            />
          </section>
        )}

        {sectionComponents}

        {isPaymentsPage && <PaymentsRateSection />}

        {actionSectionsComponents}
      </div>
    </Layout>
  );
}

export function Head({ data, pageContext }) {
  const { contentfulLandingPage } = data;
  const { seoMetadata } = contentfulLandingPage;
  const { isBusinessPage, isDirectMailPage } = pageContext;

  const faqSection = data?.contentfulLandingPage?.actionSections?.find(
    (section) =>
      section.__typename === "ContentfulGenericList" && section.listItems?.[0]?.__typename === "ContentfulListItemFaq"
  );

  const faqs = getProcessedFaqs(faqSection?.listItems || []);

  return (
    <>
      <SeoMetadata {...seoMetadata} />
      <body className={(isDirectMailPage || isBusinessPage) && c.clipHorizontalOverflow} />
      <link rel="stylesheet" href="https://use.typekit.net/bnn2pzh.css" />
      {faqs.length > 0 && (
        <script
          type="application/ld+json"
          id="faq-schema-lp"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(generateFaqSchema(faqs)) }}
        />
      )}
    </>
  );
}

export const query = graphql`
  query ($slug: String!) {
    contentfulLandingPage(slug: { eq: $slug }) {
      slug
      contentful_id
      pageVariant
      seoMetadata {
        ...seoMetadataFields
      }
      pageHeader {
        __typename
        heading
        label
        subheading {
          subheading
        }
        desktopImage {
          gatsbyImageData(width: 960, quality: 90, layout: CONSTRAINED)
        }
        mobileImage {
          gatsbyImageData(width: 330, quality: 90, layout: CONSTRAINED)
        }
        backgroundColor {
          name
          value
        }
        backgroundImage {
          gatsbyImageData(width: 1800, quality: 90, layout: CONSTRAINED)
          file {
            details {
              image {
                height
              }
            }
          }
        }
        tabletBackgroundImage {
          gatsbyImageData(width: 600, quality: 90, layout: CONSTRAINED)
          file {
            details {
              image {
                height
              }
            }
          }
        }
        mobileBackgroundImage {
          gatsbyImageData(width: 425, quality: 90, layout: CONSTRAINED)
          file {
            details {
              image {
                height
              }
            }
          }
        }

        darkBackground
        cta {
          __typename
          ... on ContentfulCtaVideoModal {
            label
            wistiaVideoId
            trackedEventName
            style
            showIcon
          }
          ... on ContentfulCtaButton {
            label
            url
            isExternal
            trackedEventName
            style
            showIcon
            openChat
          }
        }
        ctaSecondary {
          __typename
          ... on ContentfulCtaVideoModal {
            label
            wistiaVideoId
            trackedEventName
            style
            showIcon
          }
          ... on ContentfulCtaButton {
            label
            url
            isExternal
            trackedEventName
            style
            showIcon
            openChat
          }
        }
        ctaEmailCapture {
          __typename
          ... on ContentfulCtaEmailCapture {
            variant
            inputPlaceholder
            buttonText
            successMessage
          }
        }
      }
      embeddedWistiaVideoTitle
      embeddedWistiaVideoId
      sections {
        __typename
        ... on ContentfulLandingPageSectionAtAGlance {
          id
          heading
          listItems
        }
        ... on ContentfulLandingPageSection3Column3Images {
          id
          heading
          label
          subheading {
            subheading
          }
          imageOne {
            gatsbyImageData(width: 340, quality: 90, layout: CONSTRAINED)
            description
          }
          imageTwo {
            gatsbyImageData(width: 340, quality: 90, layout: CONSTRAINED)
            description
          }
          imageThree {
            gatsbyImageData(width: 340, quality: 90, layout: CONSTRAINED)
            description
          }
        }
        ... on ContentfulLandingPageSectionLogoSoup {
          id
          heading
          logos {
            description
            gatsbyImageData(height: 180, quality: 90, layout: CONSTRAINED, formats: [AUTO, WEBP, AVIF])
          }
        }
        ... on ContentfulLandingPageSection1Column1Image {
          id
          label
          heading
          subheading {
            subheading
          }
          wistiaVideoId
          desktopImage {
            gatsbyImageData(width: 960, quality: 90, layout: CONSTRAINED)
          }
          mobileImage {
            gatsbyImageData(width: 330, quality: 90, layout: CONSTRAINED)
          }
          ctas {
            __typename
            ... on ContentfulCtaVideoModal {
              id
              label
              wistiaVideoId
              trackedEventName
              style
              showIcon
            }
            ... on ContentfulCtaButton {
              id
              label
              url
              isExternal
              trackedEventName
              style
              showIcon
              openChat
            }
          }
        }
        ... on ContentfulLandingPageSection2Column1Image {
          id
          label
          heading
          shouldShowImageColumnFirst
          subheading {
            subheading
          }
          wistiaVideoId
          desktopImage {
            gatsbyImageData(width: 560, quality: 90, layout: CONSTRAINED)
          }
          mobileImage {
            gatsbyImageData(width: 330, quality: 90, layout: CONSTRAINED)
          }
          ctas {
            __typename
            ... on ContentfulCtaVideoModal {
              id
              label
              wistiaVideoId
              trackedEventName
              style
              showIcon
            }
            ... on ContentfulCtaButton {
              id
              label
              url
              isExternal
              trackedEventName
              style
              showIcon
              openChat
            }
          }
        }
        ... on ContentfulLandingPageSectionBlockquote {
          id
          author
          company
          quote {
            quote
          }
          backgroundColor {
            name
          }
          photo {
            id
            gatsbyImageData(width: 100, quality: 90, layout: CONSTRAINED)
          }
        }
        ... on ContentfulLandingPageSectionFooterCta {
          ...ctaSectionFields
        }
        ... on ContentfulLandingPageSectionCarousel {
          id
          carouselLabel
          carouselHeading
          carouselDescription {
            raw
          }
          cta {
            __typename
            ... on ContentfulCtaVideoModal {
              label
              wistiaVideoId
              trackedEventName
              style
              showIcon
            }
            ... on ContentfulCtaButton {
              label
              url
              isExternal
              trackedEventName
              style
              showIcon
              openChat
            }
          }
          imageLabels
          desktopSlides {
            id
            gatsbyImageData(width: 2080, quality: 90, layout: CONSTRAINED, formats: [AUTO, WEBP, AVIF])
          }
          mobileSlides {
            id
            gatsbyImageData(width: 950, quality: 90, layout: CONSTRAINED, formats: [AUTO, WEBP, AVIF])
          }
        }
      }
      actionSections {
        __typename
        ... on ContentfulGenericList {
          id
          heading
          listItems {
            ...listItemFaqFields
          }
        }
        ... on ContentfulExploreMangomint {
          id
          businessTypes {
            heading
            listItems {
              ... on ContentfulListItemExploreFeatures {
                title
                url
              }
            }
          }
          features {
            heading
            listItems {
              ... on ContentfulListItemExploreFeatures {
                title
                url
                category
                icon {
                  gatsbyImageData(width: 104, quality: 90, layout: CONSTRAINED)
                }
              }
            }
          }
        }
        ... on ContentfulLandingPageSectionFooterCta {
          ...ctaSectionFields
        }
        ... on ContentfulLandingPageSectionLogoSoup {
          id
          heading
          logos {
            description
            gatsbyImageData(height: 180, quality: 90, layout: CONSTRAINED, formats: [AUTO, WEBP, AVIF])
          }
        }
        ... on ContentfulLandingPageSectionEmailCapture {
          id
          heading
          buttonLabel
          emailCapture {
            __typename
            ... on ContentfulCtaEmailCapture {
              variant
              inputPlaceholder
              buttonText
              successMessage
            }
          }
          subheading {
            subheading
          }
          leftImage {
            gatsbyImageData(width: 320, quality: 90, layout: CONSTRAINED)
          }
          rightImage {
            gatsbyImageData(width: 320, quality: 90, layout: CONSTRAINED)
          }
          logo {
            gatsbyImageData(width: 160, quality: 90, layout: CONSTRAINED)
          }
        }
      }
    }
  }
`;
